import firstMissionFigma from '../assets/quest/first_mission_figma_1920x1200.png';
import alreadyDoneFigma from '../assets/quest/already_done_figma_1920x1200.png';
import invalidQrFigma from '../assets/quest/invalid_qr_figma_1920x1200.png';
/**
 * 퀘스트 안내판 배경 이미지 선행 로드.
 *
 * 🔴 왜 필요한가 (2026-08-06 실측)
 *   안내판(`MissionNoticeBoard`)은 **4초만** 떠 있는데, 배경은 CSS `background-image` 라
 *   **그 요소가 화면에 붙는 순간에야** 다운로드가 시작된다. 실측에서 `/quest-tag` 진입 시점의
 *   배경 요청은 **0건**이었다(폰트는 191ms 에 이미 요청됨). 318KB 를 4초 안에 못 받으면
 *   `background-color`(단풍 안내판 `#cb432f` — 붉은색)만 보이다 사라진다.
 *   → 현장에서 "붉은 배경만 뜬다"로 보고됐고, 캐시된 뒤엔 정상이라 **간헐적**이었다.
 *
 * 🔴 `preloadNoticeFont.js` 와 **같은 문제·같은 해법**이다.
 *   그 파일은 폰트에 대해 이 함정을 이미 적어뒀지만 배경 이미지에는 대비가 없었다.
 *   둘을 한 자리(App.js)에서 같이 호출해 "안내판이 뜨기 전에 받아 둔다"를 한 묶음으로 유지한다.
 *
 * 🔴 CSS 와 **한 쌍**이다 — `MissionNoticeBoard.css` 가 `@media (min-width: 1600px)` 로
 *   해상도별 파일을 고른다. 여기서 같은 경계·같은 파일을 쓰지 않으면 **엉뚱한 파일을 받아**
 *   프리로드가 조용히 무효가 된다(받긴 받았는데 화면이 쓰는 건 다른 파일).
 *   경계값을 바꾸면 양쪽을 같이 바꿔야 한다. 테스트가 이 일치를 고정한다.
 *
 * ⚠️ 트레이드오프: tools 는 퀘스트 태블릿 외에 운영자 PC(쿠폰·여권 등)도 쓴다. 그쪽에서도
 *   한 벌(약 0.6~1.1MB)을 받는다. 폰트 10.2MB 옆이라 증가분은 작고, 브라우저 캐시에 남는다.
 */

// 🔴 import 로 가져온다 — 문자열 경로를 적으면 webpack 해시 파일명과 어긋나 404 가 된다.
//    CSS 가 참조하는 것과 **같은 모듈**이어야 같은 URL 이 나온다.
import unregisteredFigma from '../assets/quest/unregistered_figma_1920x1200.png';
import unregistered1280 from '../assets/quest/unregistered_bg_1280x800.webp';
import unregistered1920 from '../assets/quest/unregistered_bg_1920x1200.webp';
import invalidQr1280 from '../assets/quest/invalid_qr_bg_1280x800.webp';
import invalidQr1920 from '../assets/quest/invalid_qr_bg_1920x1200.webp';

/** 🔴 `MissionNoticeBoard.css` 의 `@media (min-width: 1600px)` 와 반드시 같아야 한다. */
export const NOTICE_HI_RES_MIN_WIDTH = 1600;

/** 안내판 10종의 해상도별 배경. 키는 `MissionNoticeBoard` 의 `notice.key` 와 같다. */
export const NOTICE_BACKGROUNDS = Object.freeze({
  'already-done': Object.freeze({ lo: alreadyDoneFigma, hi: alreadyDoneFigma }),
  unregistered: Object.freeze({ lo: unregisteredFigma, hi: unregisteredFigma }),
  'invalid-qr': Object.freeze({ lo: invalidQrFigma, hi: invalidQrFigma }),
  // 회원 QR 무효 안내판 — 전용 디자인이 없어 `invalid-qr` 과 **같은 자산**을 공유한다
  // (2026-08-19). 같은 파일이라 브라우저가 중복 요청하지 않는다.
  'invalid-member-qr': Object.freeze({ lo: invalidQr1280, hi: invalidQr1920 }),
  // 첫 미션 안내판도 2026-08-06 부터 전용 배경을 쓴다(Figma 3172:40232).
  // 이전에는 테마가 없어 `.mnb` 기본 배경(done_bg)을 그대로 썼다.
  'first-mission-first': Object.freeze({ lo: firstMissionFigma, hi: firstMissionFigma }),
  // 🔴 여권 안내판 4종 + 파싱실패 1종 (2026-09-01, 설계 §4.8 (2-1) ③).
  //    자산은 위 두 벌을 **공유**하지만 프리로더는 `notice.key` 로 갈리므로 자기 항목이
  //    있어야 한다. 없으면 배경색만 잠깐 보이는 **간헐 증상**으로 나타난다(재현이 어렵다).
  'passport-unregistered': Object.freeze({ lo: unregistered1280, hi: unregistered1920 }),
  unparsable: Object.freeze({ lo: unregistered1280, hi: unregistered1920 }),
  'passport-expired': Object.freeze({ lo: invalidQr1280, hi: invalidQr1920 }),
  'passport-cancelled': Object.freeze({ lo: invalidQr1280, hi: invalidQr1920 }),
  'passport-unusable': Object.freeze({ lo: invalidQr1280, hi: invalidQr1920 }),
});

/**
 * 현재 화면이 쓸 배경 URL 목록. CSS 와 같은 경계로 고른다.
 * @param {number} viewportWidth
 * @returns {string[]}
 */
export function pickNoticeBackgrounds(viewportWidth) {
  const key = viewportWidth >= NOTICE_HI_RES_MIN_WIDTH ? 'hi' : 'lo';
  return Object.values(NOTICE_BACKGROUNDS).map((set) => set[key]);
}

/**
 * 안내판 배경을 미리 받아 브라우저 캐시에 올린다.
 *
 * @returns {Promise<number>} 실제로 로드에 성공한 개수.
 *   reject 하지 않는다 — 배경이 없어도 문구는 읽히므로 앱 기동을 막을 이유가 없다
 *   (폰트 프리로더와 같은 방침).
 */
export default function preloadNoticeAssets() {
  if (typeof window === 'undefined' || typeof Image === 'undefined') {
    return Promise.resolve(0);
  }
  const urls = pickNoticeBackgrounds(window.innerWidth || 0);
  return Promise.all(
    urls.map((src) => new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    })),
  ).then((results) => results.filter(Boolean).length);
}
